import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import type { Board, UUID } from "@/lib/types";

export function usePinnedBoards() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.pinnedBoards,
    queryFn: api.listPinnedBoards,
  });

  // Optimistic pin/unpin so the LeftRail and star button respond instantly.
  // On error we roll back and refetch.
  type Ctx = { prev: Board[] };
  const pin = useMutation<void, Error, { board: Board }, Ctx>({
    mutationFn: ({ board }) => api.pinBoard(board.id),
    onMutate: async ({ board }) => {
      await qc.cancelQueries({ queryKey: queryKeys.pinnedBoards });
      const prev = qc.getQueryData<Board[]>(queryKeys.pinnedBoards) ?? [];
      if (!prev.some((b) => b.id === board.id)) {
        qc.setQueryData<Board[]>(queryKeys.pinnedBoards, [board, ...prev]);
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKeys.pinnedBoards, ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pinnedBoards });
    },
  });

  const unpin = useMutation<void, Error, { boardId: UUID }, Ctx>({
    mutationFn: ({ boardId }) => api.unpinBoard(boardId),
    onMutate: async ({ boardId }) => {
      await qc.cancelQueries({ queryKey: queryKeys.pinnedBoards });
      const prev = qc.getQueryData<Board[]>(queryKeys.pinnedBoards) ?? [];
      qc.setQueryData<Board[]>(
        queryKeys.pinnedBoards,
        prev.filter((b) => b.id !== boardId),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKeys.pinnedBoards, ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pinnedBoards });
    },
  });

  return { ...query, pin, unpin };
}

export function isBoardPinned(pinned: Board[] | undefined, boardId: UUID): boolean {
  return Boolean(pinned?.some((b) => b.id === boardId));
}
